import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Gift, Leaf, Sparkle, Video } from "lucide-react";
import { missingProfileFields } from "@/lib/profile";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EASE = [0.22, 1, 0.36, 1];

const KINDS = [
  { id: "abonnement", title: "Abonnements", Icon: Leaf },
  { id: "programme", title: "Programmes digitaux", Icon: Sparkle },
  { id: "visio", title: "Rendez-vous en visio", Icon: Video },
  { id: "cadeau", title: "Cartes cadeau", Icon: Gift },
];

const priceLabel = (price) =>
  Number(price) > 0 ? `${Number(price).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €` : "Offert";

export default function Offres() {
  const navigate = useNavigate();
  const [offers, setOffers] = useState([]);
  const [settings, setSettings] = useState(null);
  const [busy, setBusy] = useState(null);
  // Carte cadeau : montant choisi librement par l'acheteuse (par offre)
  const [giftAmounts, setGiftAmounts] = useState({});
  const [searchParams] = useSearchParams();

  useEffect(() => {
    axios.get(`${API}/offers`).then(({ data }) => setOffers(data.offers)).catch(() => {});
    axios.get(`${API}/settings/public`).then(({ data }) => setSettings(data.settings)).catch(() => {});
    if (searchParams.get("annule") === "1") toast("Paiement interrompu — rien n'a été débité, votre semaine offerte vous attend quand vous voulez.");
  }, []);

  const choose = async (offer) => {
    if (offer.kind === "visio") {
      const m = offer.name.match(/(\d+)\s*min/);
      navigate(m ? `/reservation?duration=${m[1]}` : "/reservation");
      return;
    }
    if (Number(offer.price) === 0) {
      toast("Votre découverte est déjà active : une semaine offerte avec 7 Instants à créer librement — une seule fois, sans carte bancaire.");
      navigate("/accueil");
      return;
    }
    // Abonnement mensuel ou annuel : semaine d'essai offerte — Stripe enregistre la
    // carte, puis l'abonnement bascule automatiquement au jour 8 (résiliable avant).
    if (offer.kind === "abonnement" && /mensuel|annuel/i.test(offer.period || "")) {
      const token = localStorage.getItem("rav-token");
      if (!token) {
        navigate("/connexion?mode=login");
        return;
      }
      if (busy) return;
      setBusy(offer.id);
      try {
        const { data } = await axios.post(
          `${API}/payments/subscribe-trial`,
          { origin_url: window.location.origin, period: /annuel/i.test(offer.period || "") ? "annuel" : "mensuel" },
          { headers: { Authorization: `Bearer ${token}` } },
        );
        window.location.href = data.checkout_url;
      } catch (err) {
        setBusy(null);
        toast(err.response?.data?.detail || "La page de paiement n'a pas pu s'ouvrir — réessayez.");
      }
      return;
    }
    if (offer.kind === "cadeau") {
      // Carte cadeau : paiement unique Stripe, le code cadeau est délivré après paiement
      const token = localStorage.getItem("rav-token");
      if (!token) {
        navigate("/connexion?mode=login");
        return;
      }
      if (busy) return;
      setBusy(offer.id);
      try {
        // Montant libre (5 à 500 €), sinon le prix affiché de l'offre
        const chosen = parseFloat(giftAmounts[offer.id] || "") || offer.price;
        const { data } = await axios.post(
          `${API}/payments/buy-gift`,
          { offer_id: offer.id, origin_url: window.location.origin, amount: chosen },
          { headers: { Authorization: `Bearer ${token}` } },
        );
        window.location.href = data.checkout_url;
      } catch (err) {
        setBusy(null);
        toast(err.response?.data?.detail || "La page de paiement n'a pas pu s'ouvrir — réessayez.");
      }
      return;
    }
    if (settings?.payments_enabled) {
      // Après un achat : fiche profil complète obligatoire (sauf la photo)
      const token = localStorage.getItem("rav-token");
      if (!token) {
        navigate("/connexion?mode=login");
        return;
      }
      try {
        const { data } = await axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } });
        if (missingProfileFields(data.user).length > 0) {
          toast("Avant votre achat, complétez votre fiche Profil — tous les champs sauf la photo.");
          navigate("/profil");
          return;
        }
      } catch {
        navigate("/connexion?mode=login");
        return;
      }
      toast("Le paiement sécurisé arrive très prochainement.");
    } else {
      // MODE TEST : le paiement est court-circuité — le forfait choisi crédite directement le compte
      const token = localStorage.getItem("rav-token");
      if (!token) {
        navigate("/connexion?mode=login");
        return;
      }
      try {
        const { data } = await axios.post(`${API}/offers/${offer.id}/grant`, {}, { headers: { Authorization: `Bearer ${token}` } });
        toast(
          data.granted === "illimité"
            ? `« ${offer.name} » est crédité sur votre compte — Instants illimités.`
            : `« ${offer.name} » est crédité sur votre compte — ${data.granted} Instants.`,
        );
        navigate("/accueil");
      } catch (err) {
        toast(err.response?.data?.detail || "Le crédit n'a pas abouti.");
      }
    }
  };

  return (
    <>
      <main className="rav-page" data-testid="offres-page">
        <motion.img
          src="/logo-accueil.webp"
          alt="Respire Aime Vibre"
          className="rav-page-logo"
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: EASE }}
        />
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.1, ease: EASE }}>
          <h1 className="rav-page-title" data-testid="offres-title">Nos offres</h1>
          <p className="rav-page-sub rav-page-sub-lg" data-testid="offres-subtitle">
            Commencez doucement : une semaine de découverte offerte — 7 Instants à créer librement, une seule fois, sans carte bancaire — puis grandissez à votre rythme.
          </p>
          <p className="rav-page-sub" data-testid="offres-warm-message" style={{ fontStyle: "italic", marginTop: 6 }}>
            Et si quelques minutes par jour changeaient votre quotidien ? 🤎 Avec RESPIRE • AIME • VIBRE,
            je vous accompagne à votre rythme, à travers des Instants adaptés à vos besoins,
            pour prendre soin de votre corps et de votre équilibre, jour après jour.
          </p>

          {KINDS.map(({ id, title, Icon }) => {
            const list = offers.filter((o) => o.kind === id);
            if (!list.length) return null;
            return (
              <section key={id} data-testid={`offers-group-${id}`}>
                <h2 className="rav-section-title">{title}</h2>
                <div className="rav-offer-list">
                  {list.map((o) => (
                    <div key={o.id} className="rav-offer-card" data-testid={`offer-card-${o.id}`}>
                      <div className="rav-offer-head">
                        <span className="rav-offer-icon"><Icon size={20} strokeWidth={1.5} /></span>
                        <span className="rav-offer-name">{o.name}</span>
                        <span className="rav-offer-price" data-testid={`offer-price-${o.id}`}>
                          {priceLabel(o.price)}
                          {o.period && o.period !== "-" && <span className="rav-offer-period"> / {o.period}</span>}
                        </span>
                      </div>
                      {o.description && <p className="rav-offer-desc">{o.description}</p>}
                      {(o.tariffs || []).map((t, i) => (
                        <p key={i} className="rav-offer-tariff" data-testid={`offer-tariff-${o.id}-${i}`}>
                          {t.label ? `${t.label} — ` : ""}{priceLabel(t.price)}
                          {t.period ? ` / ${t.period}` : ""}
                          {t.valid_until ? ` · jusqu'au ${t.valid_until}` : ""}
                        </p>
                      ))}
                      {o.kind === "abonnement" && /mensuel|annuel/i.test(o.period || "") && o.price > 0 && (
                        <p className="rav-offer-desc" data-testid={`offer-trial-terms-${o.id}`} style={{ fontSize: "0.82rem", borderTop: "1px solid rgba(184,134,82,0.25)", paddingTop: 8, marginTop: 4 }}>
                          Votre semaine de découverte (7 jours, 7 Instants) est offerte sans carte bancaire.
                          L'abonnement — {priceLabel(o.price)} TTC / {/annuel/i.test(o.period || "") ? "an" : "mois"} — ne démarre que si vous le choisissez ici :
                          c'est seulement à ce moment-là que votre carte est demandée. Sans engagement, résiliable à tout moment.
                          Paiement sécurisé par carte bancaire, Apple Pay ou Google Pay.
                        </p>
                      )}
                      {o.kind === "cadeau" && (
                        <div className="rav-gift-amounts" data-testid={`gift-amounts-${o.id}`}>
                          <div className="rav-gift-presets">
                            {[25, 50, 75, 100].map((v) => (
                              <button
                                key={v}
                                type="button"
                                className={`rav-explorer-chip${String(giftAmounts[o.id] ?? 25) === String(v) ? " is-active" : ""}`}
                                data-testid={`gift-amount-${o.id}-${v}`}
                                onClick={() => setGiftAmounts({ ...giftAmounts, [o.id]: String(v) })}
                              >
                                {v} €
                              </button>
                            ))}
                          </div>
                          <label className="rav-gift-custom">
                            <span>Ou montant libre (5 à 500 €) :</span>
                            <input
                              className="rav-input"
                              type="number"
                              min="5"
                              max="500"
                              step="1"
                              placeholder="ex. 40"
                              data-testid={`gift-amount-custom-${o.id}`}
                              value={giftAmounts[o.id] ?? ""}
                              onChange={(e) => setGiftAmounts({ ...giftAmounts, [o.id]: e.target.value })}
                            />
                          </label>
                        </div>
                      )}
                      <button
                        type="button"
                        className="rav-primary-button"
                        data-testid={`offer-cta-${o.id}`}
                        disabled={busy === o.id}
                        onClick={() => choose(o)}
                      >
                        {o.kind === "visio"
                          ? "Réserver"
                          : o.kind === "cadeau"
                            ? busy === o.id
                              ? "Ouverture sécurisée…"
                              : `Offrir ${parseFloat(giftAmounts[o.id] || "") || o.price} € 🤎`
                            : o.kind === "abonnement" && /mensuel|annuel/i.test(o.period || "") && o.price > 0
                              ? busy === o.id
                                ? "Ouverture sécurisée…"
                                : `7 jours offerts, puis ${priceLabel(o.price)} / ${/annuel/i.test(o.period || "") ? "an" : "mois"}`
                              : o.price > 0
                                ? "Choisir cette offre"
                                : "Commencer"}
                      </button>
                    </div>
                  ))}
                </div>
              </section>
            );
          })}

          <button type="button" className="rav-text-link" data-testid="offres-back-link" onClick={() => navigate("/")}>
            Retour à l'accueil
          </button>
        </motion.div>
      </main>
    </>
  );
}
